import React, {useEffect, useRef, useState} from 'react';
import {handleApiResponse, logJsError, sendMessage} from '../../services/api.service';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPaperPlane} from '@fortawesome/free-regular-svg-icons/faPaperPlane';
import Message from '../../models/message.model';
import MessageModel, {ROLE} from '../../models/message.model';
import {
	getFromStore,
	saveToStore,
	StorePath,
	storeSaveAction,
	useStoreActionsFromStore,
} from '../../services/store.service';
import {OUTGOING_ACTION, snippetService} from '../../services/snippet.service';
import {addLatestMessage, handleConversion} from '../../services/message.service';
import DismissScheduler from './DismissScheduler';
import {logIconButtonClick} from '../../services/analytics.service';
import {ICON_BUTTON_CLICK_TARGET} from '../../models/cta.model';

const Input = () => {
	const storeAction = useStoreActionsFromStore(storeSaveAction);
	const messages = getFromStore(StorePath.MESSAGES) as MessageModel[];
	const conversionRecorded = getFromStore(StorePath.CONVERSION_RECORDED);
	const [message, setMessage] = useState('');
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const isSchedulerActiveInStore = getFromStore(StorePath.IS_SCHEDULER_ACTIVE) as boolean;
	const isLocationSelectionActiveInStore = getFromStore(StorePath.IS_LOCATION_SELECTION_ACTIVE) as boolean;
	const [isSchedulerActive, setIsSchedulerActive] = useState(false);
	const [isLocationSelectionActive, setIsLocationSelectionActive] = useState(false);

	useEffect((): void => {
		// Focus on the input element when the component is shown
		inputRef.current?.focus();
	}, []);

	useEffect((): void => {
		setIsSchedulerActive(isSchedulerActiveInStore);
	}, [isSchedulerActiveInStore]);

	useEffect((): void => {
		setIsLocationSelectionActive(isLocationSelectionActiveInStore);
	}, [isLocationSelectionActiveInStore]);

	const resetInputHeight = (): void => {
		if (inputRef.current) {
			inputRef.current.style.height = '';
		}
	};

	const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
		setMessage(event.target.value);
		resetInputHeight();
		event.target.style.height = `${event.target.scrollHeight}px`;
	};

	const addMessage = (message: Message): void => {
		messages.push(message);
		saveToStore(storeAction, StorePath.MESSAGES, messages);
	};

	const logChatEngagement = (): void => {
		const status = isUserFirstMessage() ? 'start' : 'engage';
		snippetService.sendMessage({action: OUTGOING_ACTION.LOG_CHAT_EVENT, payload: {status: status}});
	};

	const isUserFirstMessage = (): boolean => {
		return messages?.length <= 2; // 2 because first msg is bot, and 2nd message is the user's first message
	};

	const handleInputEnter = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
		if (event.key === 'Enter') {
			event.preventDefault();
			handleSubmit();
		}
	};

	const handleSubmit = (event?: React.FormEvent<HTMLFormElement>): void => {
		event?.preventDefault();
		if (!message.length) {
			return;
		}
		const typingTimeout = setTimeout((): void => {
			saveToStore(storeAction, StorePath.AGENT_TYPING, true);
		}, 1300);

		resetInputHeight();
		addMessage({role: ROLE.USER, text: message});

		sendMessage(message)
			.then(handleApiResponse)
			.then((data: any) => {
				clearTimeout(typingTimeout);
				addLatestMessage(data.messages, messages, storeAction);
				saveToStore(storeAction, StorePath.AGENT_TYPING, false);
				if (!conversionRecorded) {
					handleConversion(data, storeAction);
				}
			})
			.catch((error) => {
				logJsError(error);
			});
		setMessage('');
		logChatEngagement();
	};

	const handleSendButtonClick = (): void => {
		if (message.length) {
			logIconButtonClick(ICON_BUTTON_CLICK_TARGET.SEND);
		}
	};

	const dismissScheduler = (): void => {
		saveToStore(storeAction, StorePath.IS_SCHEDULER_ACTIVE, false);
	};

	return (
		<div className="bg-white border-t pt-3">
			{isSchedulerActive ? (
				<DismissScheduler onClick={dismissScheduler}/>
			) : (
				<form className="border rounded-3xl mx-4 flex items-center overflow-hidden" onSubmit={handleSubmit}
				      style={{borderColor: "#BFC4D4"}}>
					<textarea ref={inputRef} rows={1}
					          className="p-[12px] w-full pl-4 text-gray-700 focus-within:outline-hidden resize-none max-h-[120px]"
					          name="message"
					          id="message"
					          placeholder={isLocationSelectionActive ? "Pick a location..." : "Type your message here..."}
					          value={message}
					          onKeyDown={handleInputEnter}
					          onChange={handleChange}
					          disabled={isLocationSelectionActive}/>
					<button type="submit"
					        className="w-10 h-10 flex-none text-primary hover:text-primary font-bold mr-1 rounded-full my-1 cursor-pointer"
					        onClick={handleSendButtonClick}
					        disabled={isLocationSelectionActive}>
						<FontAwesomeIcon icon={faPaperPlane} size="lg"/>
					</button>
				</form>
				)
			}
		</div>
	);
};

export default Input;
